import React, { useState, useMemo, useCallback } from "react";
import { createWorkbookFromTable, textCellAttributes } from "../../utils/table_export";
import DealLink from "../deal_link";
import { INN_COLUMN_KEY } from "../../utils/report_columns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  HStack,
  useToast,
  Link,
  Collapse,
  Tooltip
} from "@chakra-ui/react";
import { useStores } from "../../store/store_context";
import SelfFilters from "./self_filters";
import ColumnValueFilter, { HeaderTitle, ResetColumnFiltersButton, useColumnFilters } from "../column_value_filter";
import { defaultCellText, rowsPassing } from "../../utils/column_value_filter";

const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
];

const PRIMARY_MANAGER_VIEWS = new Set([
  "manager-lite",
  "manager-deals",
  "manager-orgs",
  "manager-partner",
  "manager-partner-lite"
]);

const SECONDARY_MANAGER_VIEWS = new Set([
  "partner-manager",
  "departament-manager",
  "departament-manager-withoutROP"
]);

// Колонки сокращённых видов («по менеджерам»): план и отклонения, а также
// «ИНН / УНП» — как в «Оплатах», его значения видны в строках счетов под «+».
// Без него в виде по умолчанию столбца не было бы вовсе.
const LITE_VIEW_COLUMN_KEYS = ["planProfitYear", "planProfitPeriod", "planYearLambda", "planPeriodLambda", INN_COLUMN_KEY];

const TableData = observer(({ data, renderButtons }) => {
  const toast = useToast();
  const { pageStore } = useStores();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'planProfit',
    direction: 'desc',
    level: 'pivot'
  });


  const isManagerLiteView = pageStore.selected_view === "manager-lite" || pageStore.selected_view === "manager-partner-lite";

  const availableColumns = useMemo(() => {
    const filtered = pageStore.columnConfigOpenProfit.filter(
      col => (data.length > 0 && data[0].hasOwnProperty(col.key) && col.active) || (col.active && ["RightTotal", "RightAvgTotal", "LeftTotal", "LeftAvgTotal"].includes(col.key))
    );

    return filtered.map(col => ({
      ...col,
      isNumeric: ["InvAmount", "PaidEuro", "PaidRub", "PercentPayment", "DeptEuro", "DeptRub", "PercentShipment", "planProfitYear", "planYearLambda", "planProfitPeriod", "planPeriodLambda"].includes(col.key)
    }));
  }, [data, pageStore.columnConfigOpenProfit]);

  const handleSort = useCallback((key, level) => {
    setSortConfig(prev => {
      if (prev.key === key && prev.level === level) {
        return {
          key,
          level,
          direction: prev.direction === "asc" ? "desc" : "asc"
        };
      }
      // По умолчанию — убывание
      return { key, level, direction: "desc" };
    });
  }, []);

  const formatCellValue = useCallback((columnKey, value, isNumeric = false) => {
    if (columnKey === "IdDeal" && value) {
      // ID сделки — идентификатор, а не сумма: разделители разрядов и
      // висячая «;» здесь были ошибкой (78198 показывался как «78 198;»).
      return <DealLink id={value} />;
    }
    if (value == null) return "";

    if (typeof value === "number" && isNumeric) {
      return Math.round(value).toLocaleString("ru-RU");
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }

    return value || "";
  }, []);

  const cleanNumber = (value) => {
    if (typeof value === "string") {
      const cleaned = value.replace(/[^\d,.-]/g, "").replace(",", ".");
      return parseFloat(cleaned) || 0;
    }
    return parseFloat(value) || 0;
  };

  const calculateTotalProfit = (item) => {
    const RevRub = cleanNumber(item.RevRub);
    const PaidRub = cleanNumber(item.PaidRub);
    const profit = cleanNumber(item.profit);
    const UF_CRM_1756460743245 = cleanNumber(item.UF_CRM_1756460743245);

    const openProfitValue = UF_CRM_1756460743245 || 0;

    const profitabilityFact = profit && RevRub ? (profit / RevRub) * 100 : 0;
    const profitabilityOpen =
      openProfitValue && RevRub ? (openProfitValue / RevRub) * 100 : 0;
    const profitInCash = profit
      ? PaidRub * (profitabilityFact / 100)
      : (openProfitValue / RevRub) * PaidRub
    let openProfit =
      openProfitValue && !item.ClosDate
        ? openProfitValue - profitInCash
        : 0;
    let totalProfit = openProfit + profitInCash;

    if (item.ClosDate && profit == 0) {
      openProfit = openProfitValue;
      totalProfit = openProfitValue;
    }
    return totalProfit
  }


  // Фильтры страницы для строки счёта в сводных видах. Вынесены из группировки,
  // чтобы те же строки служили базой для списков значений «⏷».
  const rowPassesPageFilters = (item) => {
      if ((pageStore.selected_view == "departament-manager-withoutROP") && (item.role_name == "Руководитель отдела продаж" || item.role_name == "Руководитель удалённого отдела продаж")) {
        return false;
      };

      if (pageStore.selected_view_open?.isReturn) {
        if (!item.IsReturn) return false;
      } if (!pageStore.selected_view_open?.isNotReturn) {
        if (item.IsReturn) return false;
      } if (pageStore.selected_view_open?.isFullShipment) {
        if (item.PercentShipment !== 100) return false;
      } if (pageStore.selected_view_open?.isPartialShipment) {
        if (!item.PercentShipment || item.PercentShipment == 0) return false;
      } if (pageStore.order_num_filter) {
        if (!(item.Num &&
          item.Num.toLowerCase().includes(pageStore.order_num_filter.toLowerCase()))) return false;
      } if (pageStore.order_deal_filter) {
        if (!(item.IdDeal && item.IdDeal.toLowerCase().includes(pageStore.order_deal_filter.toLowerCase()))) return false;
      } if (pageStore.workers && !item.active) return false;

      if (pageStore.isSelf) {
        if (!(["ТД АЛЬЯНС ФОРЕСТ ООО", "АЛЬЯНС ФОРЕСТ СПБ ООО", "АРТИГЛИО ООО", "ЛОДЖИСТИК ИНДАСТРИ ООО"].includes(item.Partner))) return false;
      } else {
        if (["ТД АЛЬЯНС ФОРЕСТ ООО", "АЛЬЯНС ФОРЕСТ СПБ ООО", "АРТИГЛИО ООО", "ЛОДЖИСТИК ИНДАСТРИ ООО"].includes(item.Partner)) return false;
      }
      return true;
  };

  // Аналитический фильтр «⏷» по значениям, как в «Просроченной ДЗ»: у
  // столбцов строки счёта (расчётные итоги — нет). Отбираются исходные счета,
  // до группировки, — группы и итоги пересчитываются по прошедшим.
  const filterableKeys = availableColumns
    .map((col) => col.key)
    .filter((key) => data.length > 0 && data[0].hasOwnProperty(key));
  const filterableSet = new Set(filterableKeys);
  const [columnFilters, setColumnFilters] = useColumnFilters(data, filterableKeys);
  const openProfitCellText = useCallback((row, key) => {
    const value = row?.[key];
    const column = availableColumns.find((col) => col.key === key);
    if (column?.isNumeric && typeof value === "number") return Math.round(value).toLocaleString("ru-RU");
    return defaultCellText(row, key);
  }, [availableColumns]);
  const filteredData = useMemo(
    () => rowsPassing(data, columnFilters, openProfitCellText),
    [data, columnFilters, openProfitCellText]
  );
  const isDealView = pageStore.selected_view === "deal";
  const filterBaseRows = useMemo(
    () => (isDealView ? data : data.filter(rowPassesPageFilters)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, isDealView, pageStore.selected_view, pageStore.selected_view_open?.isReturn, pageStore.selected_view_open?.isNotReturn, pageStore.selected_view_open?.isPartialShipment, pageStore.selected_view_open?.isFullShipment, pageStore.isSelf, pageStore.order_num_filter, pageStore.order_deal_filter, pageStore.workers]
  );
  const renderValueFilter = (column) =>
    filterableSet.has(column.key) ? (
      <ColumnValueFilter
        columnKey={column.key}
        title={column.title}
        rows={filterBaseRows}
        filters={columnFilters}
        onChange={setColumnFilters}
        textOf={openProfitCellText}
      />
    ) : null;

  const formatDateKey = useCallback((key) => {
    if (key === "Все периоды") return key;

    if (pageStore.year_or_mouth_view === "month") {
      const [year, month] = key.split("-");
      return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
    }

    return `${key}`;
  }, [pageStore.year_or_mouth_view]);

  const { groupedData, allTimeKeys, totalSums, totalGlobalSum } = useMemo(() => {
    if (pageStore.selected_view === "deal") {
      setSortConfig({ key: "Date", direction: "desc", level: "detail" })
    }


    if (!pageStore.selected_view || !pageStore.year_or_mouth_view) {
      return { groupedData: null, allTimeKeys: [], totalSums: {}, totalGlobalSum: 0 };
    }

    const getPrimaryKey = (item) => {
      const keyMap = {
        "manager-lite": item.Manager,
        "manager-deals": item.Manager,
        "manager-orgs": item.Manager,
        "org-deals": item.Org,
        "manager-partner": item.Manager,
        "partner-manager": item.Partner,
        "partner": item.Partner,
        "manager-partner-lite": item.Manager,
        "departament-manager": item.departament_name,
        "departament-manager-withoutROP": item.departament_name
      };
      return keyMap[pageStore.selected_view] || "Все";
    };

    const getSecondaryKey = (item) => {
      const keyMap = {
        "manager-orgs": item.Org,
        "manager-partner": item.Partner,
        "partner-manager": item.Manager,
        "manager-partner-lite": item.Partner,
        "departament-manager": item.Manager,
        "departament-manager-withoutROP": item.Manager
      };
      return keyMap[pageStore.selected_view] || null;
    };

    const grouped = {};
    const timeKeysSet = new Set();
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1–12

    const startYear = new Date(currentYear, 0, 1);
    const endYear = new Date(currentYear, 11, 31);

    // ✅ Источник дат (день или месяц)
    const startDateRaw = pageStore.start_date_day || pageStore.start_date_month || pageStore.start_date_year;
    const endDateRaw = pageStore.end_date_day || pageStore.end_date_month || pageStore.end_date_year;

    // ✅ Если даты не заданы — берём всё до текущего месяца
    const startDate = startDateRaw
      ? new Date(startDateRaw)
      : new Date(2020, 0, 1); // 2020-01-01 (можешь поменять)

    const endDate = endDateRaw
      ? new Date(endDateRaw)
      : new Date(currentYear, currentMonth - 1, 1);

    // ✅ Получаем диапазон годов
    function getYearsInRange() {
      const years = [];
      for (let y = startDate.getFullYear(); y <= endDate.getFullYear(); y++) {
        years.push(y);
      }
      return years;
    }

    // ✅ Получаем диапазон месяцев для конкретного года
    function getMonthsForYear(year) {
      let start = 1;
      let end = 12;

      if (year === startDate.getFullYear()) {
        start = startDate.getMonth() + 1;
      }

      if (year === endDate.getFullYear()) {
        end = endDate.getMonth() + 1;
      }

      const months = [];
      for (let m = start; m <= end; m++) {
        // добавляем ведущий ноль
        months.push(String(m).padStart(2, "0"));
      }

      return months;
    }

    // ✅ ГОДОВОЙ ВИД
    if (pageStore.year_or_mouth_view === "year") {
      const years = getYearsInRange();

      years.forEach(year => {
        timeKeysSet.add(String(year));
      });
    }

    // ✅ МЕСЯЧНЫЙ ВИД
    if (pageStore.year_or_mouth_view === "month") {
      const years = getYearsInRange();

      years.forEach(year => {
        const months = getMonthsForYear(year);

        months.forEach(month => {
          const title = `${year}-${month.padStart(2, "0")}`;
          timeKeysSet.add(title);
        });
      });
    }

    filteredData.forEach(item => {

      if (!rowPassesPageFilters(item)) return;

      const primaryKey = getPrimaryKey(item);
      const secondaryKey = getSecondaryKey(item) || "Без подгруппы";
      const date = new Date(new Date(item.AccOpenDate).getTime() - 3 * 60 * 60 * 1000);

      let timeKey = "Все периоды";
      if (item.AccOpenDate === null) {
        timeKey = null
      } else if (pageStore.year_or_mouth_view === "month") {
        timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      } else if (pageStore.year_or_mouth_view === "year") {
        timeKey = `${item.AccOpenDate.split("-")[0]}`;
      }

      const totalProfit = calculateTotalProfit(item)

      if (item.inReport && timeKey) timeKeysSet.add(timeKey);

      if (!grouped[primaryKey]) {
        grouped[primaryKey] = {
          timeGroups: {},
          totalSum: 0,
          subGroups: {},
          annualPlanAchieved: false,
        };
      }

      if (item.annualPlanAchieved) {
        grouped[primaryKey].annualPlanAchieved = true;
      }

      if (item.inReport) {
        grouped[primaryKey].timeGroups[timeKey] =
          (grouped[primaryKey].timeGroups[timeKey] || 0) + (totalProfit || 0);
        grouped[primaryKey].totalSum += (totalProfit || 0);
      }

      if (!grouped[primaryKey].subGroups[secondaryKey]) {
        grouped[primaryKey].subGroups[secondaryKey] = {
          items: [],
          timeGroups: {},
          totalSum: 0,
          annualPlanAchieved: false,
        };
      }

      const subGroup = grouped[primaryKey].subGroups[secondaryKey];
      if (item.annualPlanAchieved) {
        subGroup.annualPlanAchieved = true;
      }
      if (item.inReport) {
        subGroup.items.push(item);
        subGroup.timeGroups[timeKey] = subGroup.timeGroups[timeKey] || { items: [], sum: 0 };
        subGroup.timeGroups[timeKey].items.push(item);
        subGroup.timeGroups[timeKey].sum += (totalProfit || 0);
        subGroup.totalSum += totalProfit || 0;
      }

      if (pageStore.selected_view.includes("manager")) {
        if (item.month_employment) {
          grouped[primaryKey].month_employment = item.month_employment;
          subGroup.month_employment = item.month_employment;
        }

        if (item.month_employment_all) {
          grouped[primaryKey].month_employment_all = item.month_employment_all;
          subGroup.month_employment_all = item.month_employment_all;
        }

        if (item.planProfitYear) {
          grouped[primaryKey].planProfitYear = item.planProfitYear;
          subGroup.planProfitYear = item.planProfitYear;
        }
        if (item.planYearLambda) {
          if (!grouped[primaryKey].planYearLambda) grouped[primaryKey].planYearLambda = item.planYearLambda;
          if (!subGroup.planYearLambda) subGroup.planYearLambda = item.planYearLambda;
          if (date >= startYear && date <= endYear) {
            grouped[primaryKey].planYearLambda += totalProfit || 0;
            subGroup.planYearLambda += totalProfit || 0;
          }
        }

        if (item.planProfitPeriod) {
          grouped[primaryKey].planProfitPeriod = item.planProfitPeriod;
          subGroup.planProfitPeriod = item.planProfitPeriod;
        }
        if (item.planPeriodLambda) {
          if (!grouped[primaryKey].planPeriodLambda) grouped[primaryKey].planPeriodLambda = item.planPeriodLambda;
          if (!subGroup.planPeriodLambda) subGroup.planPeriodLambda = item.planPeriodLambda;
          if (item.inReport) {
            subGroup.planPeriodLambda += totalProfit || 0;
            grouped[primaryKey].planPeriodLambda += totalProfit || 0;
          }
        }
      }

    });

    function sortDateArray(arr) {
      return arr.sort((a, b) => {
        const isAInvalid = a.startsWith("Сделка не закрыта");
        const isBInvalid = b.startsWith("Сделка не закрыта");

        // ✅ "Сделка не закрыта" всегда внизу
        if (isAInvalid && !isBInvalid) return 1;
        if (!isAInvalid && isBInvalid) return -1;
        if (isAInvalid && isBInvalid) return 0;

        // ✅ Парсим "2025-06_Июнь"
        const [aYM] = a.split("_");
        const [bYM] = b.split("_");

        const [aYear, aMonth] = aYM.split("-").map(Number);
        const [bYear, bMonth] = bYM.split("-").map(Number);

        // ✅ Сравниваем по дате
        return aYear !== bYear
          ? aYear - bYear
          : aMonth - bMonth;
      });
    }

    const allTimeKeys = sortDateArray(
      Array.from(
        new Set(
          Array.from(timeKeysSet)
            .filter(k => k !== "Все периоды")
            .sort()
        )
      )
    );



    const groupedArray = Object.entries(grouped).map(([primaryKey, group]) => {
      const subGroups = Object.entries(group.subGroups).map(([secondaryKey, subGroup]) => ({
        secondaryKey,
        ...subGroup,
        avgSum: allTimeKeys.length > 0 ? (subGroup.totalSum || 0) / allTimeKeys.length : 0,
        planPeriodLambda: subGroup.planPeriodLambda > 0 ? `+${Math.round(subGroup.planPeriodLambda).toLocaleString("ru-RU")}` : subGroup.planPeriodLambda,
        planYearLambda: subGroup.planYearLambda > 0 ? `+${Math.round(subGroup.planYearLambda).toLocaleString("ru-RU")}` : subGroup.planYearLambda
      }));

      const hasOnlyDefaultSubgroup = subGroups.length === 1 && subGroups[0].secondaryKey === "Без подгруппы";

      return {
        primaryKey,
        timeGroups: group.timeGroups,
        totalSum: group.totalSum,
        avgSum: allTimeKeys.length > 0 ? group.totalSum / allTimeKeys.length : 0,
        subGroups: hasOnlyDefaultSubgroup
          ? [{ ...subGroups[0], secondaryKey: null, autoExpanded: true }]
          : subGroups,
        month_employment: group.month_employment,
        month_employment_all: group.month_employment_all,
        planProfitPeriod: group.planProfitPeriod,
        planProfitYear: group.planProfitYear,
        planYearLambda: group.planYearLambda > 0 ? `+${Math.round(group.planYearLambda).toLocaleString("ru-RU")}` : group.planYearLambda,
        planPeriodLambda: group.planPeriodLambda > 0 ? `+${Math.round(group.planPeriodLambda).toLocaleString("ru-RU")}` : group.planPeriodLambda
      };
    });

    const totalSums = {};
    let totalGlobalSum = 0;
    groupedArray.forEach(group => {
      totalGlobalSum += group.totalSum;
      totalSums['planProfitYear'] = (totalSums['planProfitYear'] || 0) + (group.planProfitYear || 0);
      totalSums['planProfitPeriod'] = (totalSums['planProfitPeriod'] || 0) + (group.planProfitPeriod || 0);
      totalSums['planYearLambda'] = (totalSums['planYearLambda'] || 0) + (cleanNumber(group.planYearLambda) || 0);
      totalSums['planPeriodLambda'] = (totalSums['planPeriodLambda'] || 0) + (cleanNumber(group.planPeriodLambda) || 0);
      Object.entries(group.timeGroups).forEach(([timeKey, sum]) => {
        totalSums[timeKey] = (totalSums[timeKey] || 0) + sum;
      });
    });


    return {
      groupedData: groupedArray,
      allTimeKeys,
      totalSums,
      totalGlobalSum,
    };
  }, [filteredData, pageStore.selected_view, pageStore.year_or_mouth_view, pageStore.selected_view_open?.isReturn, pageStore.selected_view_open?.isNotReturn, pageStore.selected_view_open?.isPartialShipment, pageStore.selected_view_open?.isFullShipment, pageStore.isSelf, pageStore.order_num_filter, pageStore.order_deal_filter, pageStore.workers]);

  const DATE_SORT_KEYS = useMemo(
    () => new Set(["Date", "AccOpenDate", "PlanClosDate", "ClosDate"]),
    []
  );

  const isNumericSortKey = useCallback((key) => {
    if (DATE_SORT_KEYS.has(key)) return false;
    if (key === "IdDeal") return true;
    return !!availableColumns.find((c) => c.key === key)?.isNumeric;
  }, [availableColumns, DATE_SORT_KEYS]);

  const getGroupItems = useCallback((groupOrSub) => {
    if (Array.isArray(groupOrSub?.items)) return groupOrSub.items;
    if (Array.isArray(groupOrSub?.subGroups)) {
      return groupOrSub.subGroups.flatMap((sg) => sg.items || []);
    }
    return [];
  }, []);

  const getDetailSortValue = useCallback((item, key) => {
    if (
      key === "planProfit" ||
      key === "group-total" ||
      key === "LeftTotal" ||
      key === "RightTotal"
    ) {
      return calculateTotalProfit(item) || 0;
    }

    if (
      key === "group-avg" ||
      key === "LeftAvgTotal" ||
      key === "RightAvgTotal"
    ) {
      const profit = calculateTotalProfit(item) || 0;
      return allTimeKeys.length > 0 ? profit / allTimeKeys.length : 0;
    }

    if (key.startsWith("period||")) {
      const [, mode, periodName] = key.split("||");
      const date = item.AccOpenDate ? new Date(item.AccOpenDate) : null;
      let itemPeriodKey;

      if (item.AccOpenDate == null) {
        itemPeriodKey = "Не открыт";
      } else if (pageStore.year_or_mouth_view === "month") {
        itemPeriodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      } else {
        itemPeriodKey = `${date.getFullYear()}`;
      }

      if (periodName !== itemPeriodKey) return 0;

      const profit = calculateTotalProfit(item) || 0;
      if (mode === "abs" || mode === "percent") return profit;
      if (mode === "part") {
        return totalSums[itemPeriodKey] ? (profit / totalSums[itemPeriodKey]) * 100 : 0;
      }
      return 0;
    }

    const value = item[key];

    if (DATE_SORT_KEYS.has(key)) {
      if (!value) return null;
      const ts = new Date(value).getTime();
      return Number.isNaN(ts) ? null : ts;
    }

    if (isNumericSortKey(key)) {
      return cleanNumber(value);
    }

    if (value == null || value === "") return "";
    return value;
  }, [DATE_SORT_KEYS, isNumericSortKey, pageStore.year_or_mouth_view, totalSums, allTimeKeys.length]);

  const getSortValue = useCallback((item, key, level) => {
    if (level === "pivot") {
      switch (key) {
        case "group-name":
          return item.primaryKey || item.secondaryKey || "";
        case "group-total":
        case "planProfit":
        case "LeftTotal":
        case "RightTotal":
          return Number(item.totalSum) || 0;
        case "group-avg":
        case "LeftAvgTotal":
        case "RightAvgTotal": {
          if (typeof item.avgSum === "number" && !Number.isNaN(item.avgSum)) {
            return item.avgSum;
          }
          const total = Number(item.totalSum) || 0;
          return allTimeKeys.length > 0 ? total / allTimeKeys.length : 0;
        }
        case "planProfitYear":
          return cleanNumber(item.planProfitYear);
        case "planYearLambda":
          return cleanNumber(item.planYearLambda);
        case "planProfitPeriod":
          return cleanNumber(item.planProfitPeriod);
        case "planPeriodLambda":
          return cleanNumber(item.planPeriodLambda);
        default: {
          if (key.startsWith("period||")) {
            const [, mode, periodName] = key.split("||");
            if (!item.timeGroups) return 0;
            const periodValue = item.timeGroups[periodName];
            const prevKey = allTimeKeys[allTimeKeys.indexOf(periodName) - 1];
            const prevPeriodValue = item.timeGroups[prevKey];

            const currentSum =
              typeof periodValue === "object" && periodValue !== null
                ? periodValue.sum || 0
                : periodValue || 0;
            const prevSum =
              typeof prevPeriodValue === "object" && prevPeriodValue !== null
                ? prevPeriodValue.sum || 0
                : prevPeriodValue || 0;

            if (mode === "abs") return currentSum;
            if (mode === "percent") {
              return prevSum ? ((currentSum - prevSum) / prevSum) * 100 : 0;
            }
            if (mode === "part") {
              return totalSums[periodName] ? (currentSum / totalSums[periodName]) * 100 : 0;
            }
            return 0;
          }

          // Обычные колонки: число/дату агрегируем по строкам группы.
          // Текст: группы/подгруппы — по имени, сделки — на detail по полю.
          const items = getGroupItems(item);

          if (isNumericSortKey(key)) {
            return items.reduce((sum, row) => sum + cleanNumber(row[key]), 0);
          }

          if (DATE_SORT_KEYS.has(key)) {
            const timestamps = items
              .map((row) => (row[key] ? new Date(row[key]).getTime() : NaN))
              .filter((ts) => !Number.isNaN(ts));
            return timestamps.length ? Math.min(...timestamps) : null;
          }

          if (item.secondaryKey != null) return item.secondaryKey;
          return item.primaryKey ?? "";
        }
      }
    }

    return getDetailSortValue(item, key);
  }, [
    allTimeKeys,
    totalSums,
    getGroupItems,
    isNumericSortKey,
    DATE_SORT_KEYS,
    getDetailSortValue,
  ]);

  const sortedGroupedData = useMemo(() => {
    if (!groupedData) return null;

    const isEmpty = (value) =>
      value === null || value === undefined || value === "";

    const compareItems = (a, b, key, level) => {
      const valueA = getSortValue(a, key, level);
      const valueB = getSortValue(b, key, level);

      if (isEmpty(valueA) && isEmpty(valueB)) return 0;
      if (isEmpty(valueA)) return 1;
      if (isEmpty(valueB)) return -1;

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
      }

      return sortConfig.direction === "asc"
        ? String(valueA).localeCompare(String(valueB), "ru", { numeric: true, sensitivity: "base" })
        : String(valueB).localeCompare(String(valueA), "ru", { numeric: true, sensitivity: "base" });
    };

    const sortedGroups = [...groupedData].sort((a, b) =>
      compareItems(a, b, sortConfig.key, "pivot")
    );

    return sortedGroups.map((group) => ({
      ...group,
      subGroups: [...group.subGroups]
        .sort((a, b) => compareItems(a, b, sortConfig.key, "pivot"))
        .map((subGroup) => ({
          ...subGroup,
          items: [...subGroup.items].sort((a, b) =>
            compareItems(a, b, sortConfig.key, "detail")
          ),
        })),
    }));
  }, [groupedData, sortConfig, getSortValue]);

  const handleExportToXLSX = useCallback(async () => {
    var can = await pageStore.CheckPermission("export excel")
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    if (!groupedData) return;

    const table = document.querySelector("table");
    if (!table) return;

    const workbook = createWorkbookFromTable(table);

    // Сохраняем файл
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "table.xlsx");

  }, [groupedData, allTimeKeys, totalSums, totalGlobalSum, availableColumns, formatCellValue, formatDateKey, isManagerLiteView]);

  const toggleExpand = useCallback((type, key, subKey = null) => {
    const compositeKey = subKey ? `${key}__${subKey}` : key;
    setExpandedGroups(prev => ({
      ...prev,
      [compositeKey]: !prev[compositeKey]
    }));
  }, []);

  const expandAll = useCallback(() => {
    const newExpanded = {};
    groupedData?.forEach(group => {
      newExpanded[group.primaryKey] = true;
      group.subGroups.forEach(subGroup => {
        newExpanded[`${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`] = true;
      });
    });
    setExpandedGroups(newExpanded);
  }, [groupedData]);

  const collapseAll = useCallback(() => {
    setExpandedGroups({});
  }, []);

  const renderColumns = () =>
    pageStore.year_or_mouth_view &&
    allTimeKeys.map(timeKey => (
      <React.Fragment key={timeKey}>
        {pageStore.absolute_column_openProfit && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||abs||${timeKey}`, 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            {formatDateKey(timeKey)}
            {sortConfig.key === `period||abs||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}

        {pageStore.percent_column_openProfit && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||percent||${timeKey}`, 'pivot')}
            cursor="pointer"
          >
            {pageStore.absolute_column_openProfit
              ? "%"
              : `${formatDateKey(timeKey)} %`}
            {sortConfig.key === `period||percent||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}

        {pageStore.part_column_openProfit && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||part||${timeKey}`, 'pivot')}
            cursor="pointer"
          >
            Доли, %
            {sortConfig.key === `period||part||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}
      </React.Fragment>
    ));


  const renderTableHeader = () => (
    <Thead bgColor={"black"} position={"sticky"} top={"0px"} zIndex={11}>
      <Tr>
        <Th color="white" px={0} py={pageStore.rowHeight} />
        <Th
          color="white"
          onClick={() => handleSort('group-name', 'pivot')}
          cursor="pointer"
          py={pageStore.rowHeight}
        >
          Группа
          {sortConfig.key === 'group-name' && sortConfig.level === 'pivot' && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>
        {availableColumns.map(item => item.key).includes("LeftTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('planProfit', 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            Итого
            {sortConfig.key === 'planProfit' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
        {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('group-avg', 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            Итого среднее
            {sortConfig.key === 'group-avg' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
        {!isManagerLiteView && availableColumns
          .filter(col => !["planProfit", "LeftTotal", "RightTotal", "RightAvgTotal", "LeftAvgTotal"].includes(col.key))
          .map(column => (
            <Th
              key={column.key}
              color="white"
              px={4}
              textAlign={column.isNumeric ? "right" : "left"}
              onClick={() => handleSort(column.key, 'pivot')}
              cursor="pointer"
              py={pageStore.rowHeight}
            >
              <HeaderTitle
                title={column.title}
                suffix={sortConfig.key === column.key && sortConfig.level === 'pivot' && (
                  <Box as="span" ml={2}>
                    {sortConfig.direction === "asc" ? "↑" : "↓"}
                  </Box>
                )}
                filter={renderValueFilter(column)}
              />
            </Th>
          ))}
        {isManagerLiteView && availableColumns
          .filter(col => LITE_VIEW_COLUMN_KEYS.includes(col.key))
          .map(column => (
            <Th
              key={column.key}
              color="white"
              px={4}
              textAlign={column.isNumeric ? "right" : "left"}
              onClick={() => handleSort(column.key, 'pivot')}
              cursor="pointer"
              py={pageStore.rowHeight}
            >
              <HeaderTitle
                title={column.title}
                suffix={sortConfig.key === column.key && sortConfig.level === 'pivot' && (
                  <Box as="span" ml={2}>
                    {sortConfig.direction === "asc" ? "↑" : "↓"}
                  </Box>
                )}
                filter={renderValueFilter(column)}
              />
            </Th>
          ))}
        {renderColumns()}
        {availableColumns.map(item => item.key).includes("RightTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('planProfit', 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            Итого
            {sortConfig.key === 'planProfit' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
        {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('group-avg', 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            Итого среднее
            {sortConfig.key === 'group-avg' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
      </Tr>
    </Thead>
  );

  const renderTotalRow = () => (
    <Tr bg="#555" py={pageStore.rowHeight}>
      <Td px={0} py={0} />
      <Td fontWeight="bold" color="white" >Итого {sortedGroupedData.length}</Td>
      {availableColumns.map(item => item.key).includes("LeftTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
          {formatCellValue("", totalGlobalSum, true)}
        </Td>
        : <></>}
      {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
          {formatCellValue("", allTimeKeys.length > 0 ? totalGlobalSum / allTimeKeys.length : 0, true)}
        </Td>
        : <></>}
      {!isManagerLiteView && availableColumns
        .filter(col => !["planProfit", "RightTotal", "LeftTotal", "RightAvgTotal", "LeftAvgTotal"].includes(col.key))
        .map(column => (["planProfitYear", "planProfitPeriod", "planYearLambda", "planPeriodLambda"].includes(column.key)) ? <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight} color={
          !["planPeriodLambda", "planYearLambda"].includes(column.key)
            ? "white"
            : (totalSums[column.key] < 0 ? "rgb(255, 126, 126)" : "rgb(169, 255, 116)")
        }>
          {formatCellValue("", totalSums[column.key] || 0, true)}
        </Td> : (
          <Th key={column.key} color="white" px={4} textAlign={column.isNumeric ? "right" : "left"} py={pageStore.rowHeight} />
        ))}
      {isManagerLiteView && availableColumns
        .filter(col => LITE_VIEW_COLUMN_KEYS.includes(col.key))
        .map(column => (["planProfitYear", "planProfitPeriod", "planYearLambda", "planPeriodLambda"].includes(column.key)) ? <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight} color={
          !["planPeriodLambda", "planYearLambda"].includes(column.key)
            ? "white"
            : (totalSums[column.key] < 0 ? "rgb(255, 126, 126)" : "rgb(169, 255, 116)")
        }>
          {formatCellValue("", totalSums[column.key] || 0, true)}
        </Td> : (
          <Th key={column.key} color="white" px={4} textAlign={column.isNumeric ? "right" : "left"} py={pageStore.rowHeight} />
        ))}
      {pageStore.year_or_mouth_view &&
        allTimeKeys.map((timeKey, i) => {
          const current = totalSums[timeKey] ?? 0;
          const prevKey = allTimeKeys[i - 1];
          const prev = prevKey ? totalSums[prevKey] ?? 0 : 0;

          return (
            <React.Fragment key={timeKey}>
              {pageStore.absolute_column_openProfit && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  {formatCellValue(`absolute-period-${timeKey}`, current, true)}
                </Td>
              )}

              {pageStore.percent_column_openProfit && (() => {
                if (prev === 0) {
                  return (
                    <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                      —
                    </Td>
                  );
                }

                const ratio = (current - prev) / prev; // относительное изменение
                const percent = ratio * 100;

                let color = "white";
                let sign = "";

                if (ratio < 0) {
                  color = "red.400";
                  sign = "−";
                } else if (ratio < 0.2) {
                  color = "yellow.400";
                  sign = "+";
                } else {
                  color = "green.300"; // яркосалатовый
                  sign = "+";
                }

                return (
                  <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                    {sign}
                    {formatCellValue(`percent-period-${timeKey}`, Math.abs(percent), true)}
                  </Td>
                );
              })()}

              {pageStore.part_column_openProfit && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  100%
                </Td>
              )}
            </React.Fragment>
          );
        })}
      {availableColumns.map(item => item.key).includes("RightTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
          {formatCellValue("", totalGlobalSum, true)}
        </Td>
        : <></>}
      {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
          {formatCellValue("", allTimeKeys.length > 0 ? totalGlobalSum / allTimeKeys.length : 0, true)}
        </Td>
        : <></>}
    </Tr>
  );

  const renderGroupRow = (group) => {
    const isExpanded = expandedGroups[group.primaryKey];
    const annualPlanAchieved =
      PRIMARY_MANAGER_VIEWS.has(pageStore.selected_view) &&
      (group.annualPlanAchieved ||
        (cleanNumber(group.planProfitYear) > 0 && cleanNumber(group.planYearLambda) >= 0));

    return (
      <React.Fragment key={group.primaryKey}>
        <Tr bg="#333" py={pageStore.rowHeight}>
          <Td px={0} py={0}>
            <Button
              size="sm"
              fontSize="20px"
              bg="#333"
              color="white"
              _hover={{ bg: "#222" }}
              onClick={() => toggleExpand('group', group.primaryKey)}
            >
              {isExpanded ? "−" : "+"}
            </Button>
          </Td>
          {pageStore.selected_view.startsWith("manager") ? <Tooltip label={group.month_employment + " мес. / " + group.month_employment_all + " мес."} placement="top" hasArrow>
            <Td
              cursor="pointer"
              onClick={() => toggleExpand('group', group.primaryKey)}
              fontWeight="bold"
              color={annualPlanAchieved ? "green.300" : "white"}
              py={pageStore.rowHeight}
            >
              {group.primaryKey}{annualPlanAchieved ? " ⭐" : ""}
            </Td></Tooltip> :
            <Td
              cursor="pointer"
              onClick={() => toggleExpand('group', group.primaryKey)}
              fontWeight="bold"
              color="white"
              py={pageStore.rowHeight}
            >
              {group.primaryKey}
            </Td>
          }
          {availableColumns.map(item => item.key).includes("LeftTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
              {formatCellValue("total", group.totalSum, true)}
            </Td>
            : <></>}
          {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
              {formatCellValue("avg", group.avgSum, true)}
            </Td>
            : <></>}
          {!isManagerLiteView && availableColumns
            .filter(col => !["planProfit", "LeftTotal", "LeftAvgTotal", "RightTotal", "RightAvgTotal"].includes(col.key))
            .map(column =>
              ["planProfitPeriod", "planProfitYear", "planYearLambda", "planPeriodLambda"].includes(column.key) &&
                group[column.key] !== undefined && pageStore.selected_view.startsWith("manager") ? (
                <Td
                  key={column.key}
                  color={
                    !["planPeriodLambda", "planYearLambda"].includes(column.key)
                      ? "white"
                      : (group[column.key] < 0 ? "rgb(255, 126, 126)" : "rgb(169, 255, 116)")
                  }
                  px={4}
                  textAlign={column.isNumeric ? "right" : "left"}
                  py={pageStore.rowHeight}
                >
                  {formatCellValue(column.key, group[column.key], true)}
                </Td>
              ) : (
                <Th
                  key={column.key}
                  color="white"
                  px={4}
                  textAlign={column.isNumeric ? "right" : "left"}
                  py={pageStore.rowHeight}
                />
              )
            )}
          {isManagerLiteView && availableColumns
            .filter(col => LITE_VIEW_COLUMN_KEYS.includes(col.key))
            .map(column =>
              group[column.key] !== undefined ? (
                <Td
                  key={column.key}
                  color={
                    !["planPeriodLambda", "planYearLambda"].includes(column.key)
                      ? "white"
                      : (group[column.key] < 0 ? "rgb(255, 126, 126)" : "rgb(169, 255, 116)")
                  }
                  px={4}
                  textAlign={column.isNumeric ? "right" : "left"}
                  py={pageStore.rowHeight}
                >
                  {formatCellValue(column.key, group[column.key], true)}
                </Td>
              ) : (
                <Th
                  key={column.key}
                  color="white"
                  px={4}
                  textAlign={column.isNumeric ? "right" : "left"}
                  py={pageStore.rowHeight}
                />
              )
            )}
          {pageStore.year_or_mouth_view &&
            allTimeKeys.map((timeKey, i) => {
              const current = group.timeGroups[timeKey] ?? 0;
              const prevKey = allTimeKeys[i - 1];
              const prev = prevKey ? group.timeGroups[prevKey] ?? 0 : 0;

              return (
                <React.Fragment key={timeKey}>
                  {pageStore.absolute_column_openProfit && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(`absolute-period-${timeKey}`, current, true)}
                    </Td>
                  )}

                  {pageStore.percent_column_openProfit && (() => {
                    if (prev === 0) {
                      return (
                        <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                          —
                        </Td>
                      );
                    }

                    const ratio = (current - prev) / prev; // относительное изменение
                    const percent = ratio * 100;

                    let color = "white";
                    let sign = "";

                    if (ratio < 0) {
                      color = "red.400";
                      sign = "−";
                    } else if (ratio < 0.2) {
                      color = "yellow.400";
                      sign = "+";
                    } else {
                      color = "green.300"; // яркосалатовый
                      sign = "+";
                    }

                    return (
                      <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                        {sign}
                        {formatCellValue(`percent-period-${timeKey}`, Math.abs(percent), true)}
                      </Td>
                    );
                  })()}

                  {pageStore.part_column_openProfit && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(`absolute-period-${timeKey}`, current / (totalSums[timeKey] || 0) * 100, true)}
                    </Td>
                  )}
                </React.Fragment>
              );
            })}
          {availableColumns.map(item => item.key).includes("RightTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
              {formatCellValue("total", group.totalSum, true)}
            </Td>
            : <></>}
          {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
              {formatCellValue("avg", group.avgSum, true)}
            </Td>
            : <></>}
        </Tr>

        {isExpanded && group.subGroups.map(renderSubGroupRow(group))}
      </React.Fragment>
    );
  };

  const renderSubGroupRow = (group) => (subGroup) => {
    const compositeKey = `${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`;
    const isExpanded = expandedGroups[compositeKey] || subGroup.autoExpanded;
    const subGroupAvg = allTimeKeys.length > 0 ? subGroup.totalSum / allTimeKeys.length : 0;
    const isTooltip = ["partner-manager", "departament-manager", "departament-manager-withoutROP"].includes(pageStore.selected_view)
    const annualPlanAchieved =
      SECONDARY_MANAGER_VIEWS.has(pageStore.selected_view) &&
      (subGroup.annualPlanAchieved ||
        (cleanNumber(subGroup.planProfitYear) > 0 && cleanNumber(subGroup.planYearLambda) >= 0));

    return (
      <React.Fragment key={compositeKey}>
        {subGroup.secondaryKey && (
          <Tr bg="#444" py={pageStore.rowHeight}>
            <Td px={0} py={0}>
              <Button
                color="white"
                fontSize="25px"
                _hover={{ bg: "black", color: "white" }}
                size="sm"
                bg="#444"
                onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
              >
                {isExpanded ? "−" : "+"}
              </Button>
            </Td>
            {isTooltip ? <Tooltip label={subGroup.month_employment + " мес. / " + subGroup.month_employment_all + " мес."} placement="top" hasArrow>
              <Td
                pl={12}
                cursor="pointer"
                onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
                fontWeight="bold"
                color={annualPlanAchieved ? "green.300" : "white"}
                py={pageStore.rowHeight}
              >
                {subGroup.secondaryKey}{annualPlanAchieved ? " ⭐" : ""}
              </Td></Tooltip> :
              <Td
                pl={12}
                cursor="pointer"
                onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
                fontWeight="bold"
                py={pageStore.rowHeight}
              >
                {subGroup.secondaryKey}
              </Td>
            }
            {availableColumns.map(item => item.key).includes("LeftTotal") ?
              <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight}>
                {formatCellValue("total", subGroup.totalSum, true)}
              </Td>
              : <></>}
            {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
              <Td textAlign="right" py={pageStore.rowHeight}>
                {formatCellValue("avg", subGroupAvg, true)}
              </Td>
              : <></>}
            {!isManagerLiteView && availableColumns
              .filter(col => !["planProfit", "LeftTotal", "LeftAvgTotal", "RightTotal", "RightAvgTotal"].includes(col.key))
              .map(column =>
                ["planProfitPeriod", "planProfitYear", "planYearLambda", "planPeriodLambda"].includes(column.key) &&
                  group[column.key] !== undefined ? (
                  <Td
                    key={column.key}
                    color={
                      !["planPeriodLambda", "planYearLambda"].includes(column.key)
                        ? "white"
                        : (group[column.key] < 0 ? "rgb(255, 126, 126)" : "rgb(169, 255, 116)")
                    }
                    px={4}
                    textAlign={column.isNumeric ? "right" : "left"}
                    py={pageStore.rowHeight}
                  >
                    {formatCellValue(column.key, group[column.key], true)}
                  </Td>
                ) : (
                  <Th
                    key={column.key}
                    color="white"
                    px={4}
                    textAlign={column.isNumeric ? "right" : "left"}
                    py={pageStore.rowHeight}
                  />
                )
              )}
            {isManagerLiteView && availableColumns
              .filter(col => LITE_VIEW_COLUMN_KEYS.includes(col.key))
              .map(column =>
                group[column.key] !== undefined ? (
                  <Td
                    key={column.key}
                    color={
                      !["planPeriodLambda", "planYearLambda"].includes(column.key)
                        ? "white"
                        : (group[column.key] < 0 ? "rgb(255, 126, 126)" : "rgb(169, 255, 116)")
                    }
                    px={4}
                    textAlign={column.isNumeric ? "right" : "left"}
                    py={pageStore.rowHeight}
                  >
                    {formatCellValue(column.key, group[column.key], true)}
                  </Td>
                ) : (
                  <Th
                    key={column.key}
                    color="white"
                    px={4}
                    textAlign={column.isNumeric ? "right" : "left"}
                    py={pageStore.rowHeight}
                  />
                )
              )}
            {pageStore.year_or_mouth_view &&
              allTimeKeys.map((timeKey, i) => {
                const current = subGroup.timeGroups[timeKey]?.sum ?? 0;
                const prevKey = allTimeKeys[i - 1];
                const prev = prevKey ? subGroup.timeGroups[prevKey]?.sum ?? 0 : 0;

                return (
                  <React.Fragment key={timeKey}>
                    {pageStore.absolute_column_openProfit && (
                      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                        {formatCellValue(`absolute-period-${timeKey}`, current, true)}
                      </Td>
                    )}

                    {pageStore.percent_column_openProfit && (() => {
                      if (prev === 0) {
                        return (
                          <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                            —
                          </Td>
                        );
                      }

                      const ratio = (current - prev) / prev; // относительное изменение
                      const percent = ratio * 100;

                      let color = "white";
                      let sign = "";

                      if (ratio < 0) {
                        color = "red.400";
                        sign = "−";
                      } else if (ratio < 0.2) {
                        color = "yellow.400";
                        sign = "+";
                      } else {
                        color = "green.300"; // яркосалатовый
                        sign = "+";
                      }

                      return (
                        <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                          {sign}
                          {formatCellValue(`percent-period-${timeKey}`, Math.abs(percent), true)}
                        </Td>
                      );
                    })()}

                    {pageStore.part_column_openProfit && (
                      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                        {formatCellValue(`absolute-period-${timeKey}`, current / (totalSums[timeKey] || 0) * 100, true)}
                      </Td>
                    )}

                  </React.Fragment>
                );
              })}
            {availableColumns.map(item => item.key).includes("RightTotal") ?
              <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight}>
                {formatCellValue("total", subGroup.totalSum, true)}
              </Td>
              : <></>}
            {availableColumns.map(item => item.key).includes("RightAvgTotal") ?

              <Td textAlign="right" py={pageStore.rowHeight}>
                {formatCellValue("avg", subGroupAvg, true)}
              </Td>
              : <></>}
          </Tr>
        )}

        {isExpanded && subGroup.items.map((item, idx) =>
          renderDetailRow(item, idx, subGroup.items.length)
        )}
      </React.Fragment>
    );
  };

  const renderDetailRow = (item, index, totalItems) => {
    let rowTimeKey = pageStore.year_or_mouth_view === "month"
      ? `${new Date(item.AccOpenDate).getFullYear()}-${String(new Date(item.AccOpenDate).getMonth() + 1).padStart(2, "0")}`
      : `${new Date(item.AccOpenDate).getFullYear()}`;

    if (item.AccOpenDate === null) {
      rowTimeKey = "Не открыт";
    }

    return (
      <Tr key={index} bg={index % 2 === 0 ? "#555" : "#666"} py={pageStore.rowHeight}>
        <Td px={4} py={pageStore.rowHeight} />
        <Td px={4} py={pageStore.rowHeight} />
        {availableColumns.map(item => item.key).includes("LeftTotal") ?
          <Td px={4} textAlign="right" fontStyle={item.active ? "normal" : "italic"} py={pageStore.rowHeight}>
            {formatCellValue("total", calculateTotalProfit(item), true)}
          </Td>
          : <></>}
        {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
          <Td px={4} textAlign="right" fontStyle={item.active ? "normal" : "italic"} py={pageStore.rowHeight}>
            {formatCellValue("avg", allTimeKeys.length > 0 ? calculateTotalProfit(item) / allTimeKeys.length : 0, true)}
          </Td>
          : <></>}

        {!isManagerLiteView &&
          availableColumns
            .filter(col =>
              !["planProfit", "LeftTotal", "LeftAvgTotal", "RightAvgTotal", "RightTotal"]
                .includes(col.key)
            )
            .map(column =>
              column.key === "Manager" && item.month_employment ? (
                <Tooltip
                  key={column.key}
                  label={`${item.month_employment} мес. / ${item.month_employment_all} мес.`}
                  placement="top"
                  hasArrow
                >
                  <Td
                    px={4}
                    py={pageStore.rowHeight}
                    textAlign={column.isNumeric ? "right" : "left"}
                    fontStyle={item.active ? "normal" : "italic"}
                  >
                    {formatCellValue(column.key, item[column.key], column.isNumeric)}
                  </Td>
                </Tooltip>
              ) : (
                <Td
                  key={column.key}
                  px={4}
                  py={pageStore.rowHeight}
                  textAlign={column.isNumeric ? "right" : "left"}
                  fontStyle={item.active ? "normal" : "italic"}
                  {...textCellAttributes(item[column.key], column)}
                >
                  {formatCellValue(column.key, item[column.key], column.isNumeric)}
                </Td>
              )
            )}
        {isManagerLiteView &&
          availableColumns
            .filter(col => LITE_VIEW_COLUMN_KEYS.includes(col.key))
            .map(column =>
              item[column.key] !== undefined ? (
                <Td
                  key={column.key}
                  px={4}
                  py={pageStore.rowHeight}
                  textAlign={column.isNumeric ? "right" : "left"}
                  fontStyle={item.active ? "normal" : "italic"}
                  {...textCellAttributes(item[column.key], column)}
                >
                  {formatCellValue(column.key, item[column.key], column.isNumeric)}
                </Td>
              ) : null
            )}
        {pageStore.year_or_mouth_view && pageStore.absolute_column_openProfit && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} textAlign="right" fontStyle={item.active ? "normal" : "italic"} py={pageStore.rowHeight}>
            {timeKey === rowTimeKey ? formatCellValue("total", calculateTotalProfit(item), true) : "0"}
          </Td>
        ))}
        {pageStore.year_or_mouth_view && pageStore.percent_column_openProfit && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} textAlign="right" fontStyle={item.active ? "normal" : "italic"} py={pageStore.rowHeight}>
            {timeKey === rowTimeKey ? formatCellValue("total", calculateTotalProfit(item), true) : "0"}
          </Td>
        ))}
        {pageStore.year_or_mouth_view && pageStore.part_column_openProfit && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} textAlign="right" fontStyle={item.active ? "normal" : "italic"} py={pageStore.rowHeight}>
            {timeKey === rowTimeKey ? formatCellValue("total", calculateTotalProfit(item) / (totalSums[timeKey] || 0) * 100, true) : "0"}
          </Td>
        ))}


        {availableColumns.map(item => item.key).includes("RightTotal") ?
          <Td px={4} textAlign="right" fontStyle={item.active ? "normal" : "italic"} py={pageStore.rowHeight}>
            {formatCellValue("total", calculateTotalProfit(item), true)}
          </Td>
          : <></>}
        {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
          <Td px={4} textAlign="right" fontStyle={item.active ? "normal" : "italic"} py={pageStore.rowHeight}>
            {formatCellValue("avg", allTimeKeys.length > 0 ? calculateTotalProfit(item) / allTimeKeys.length : 0, true)}
          </Td>
          : <></>}
      </Tr>
    );
  };

  const sortedFlatData = useMemo(() => {
    if (!filteredData?.length || !sortConfig?.key) return filteredData || [];

    const isEmpty = (value) =>
      value === null || value === undefined || value === "";

    return [...filteredData].sort((a, b) => {
      const valueA = getDetailSortValue(a, sortConfig.key);
      const valueB = getDetailSortValue(b, sortConfig.key);

      if (isEmpty(valueA) && isEmpty(valueB)) return 0;
      if (isEmpty(valueA)) return 1;
      if (isEmpty(valueB)) return -1;

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
      }

      return sortConfig.direction === "asc"
        ? String(valueA).localeCompare(String(valueB), "ru", { numeric: true, sensitivity: "base" })
        : String(valueB).localeCompare(String(valueA), "ru", { numeric: true, sensitivity: "base" });
    });
  }, [filteredData, sortConfig, getDetailSortValue]);

  const renderFlatTable = () => (
    <Table tableLayout="fixed" width="100%" minWidth="800px" variant="simple" bg="" color="white">
      <Thead>
        <Tr py={pageStore.rowHeight}>
          {availableColumns.map(column => (
            <Th
              key={column.key}
              color="white"
              onClick={() => handleSort(column.key, 'detail')}
              cursor="pointer"
              px={4}
              py={pageStore.rowHeight}

              textAlign={column.isNumeric ? "right" : "left"}
            >
              <HeaderTitle
                title={column.title}
                suffix={sortConfig.key === column.key && sortConfig.level === 'detail' && (
                  <Box as="span" ml={2}>
                    {sortConfig.direction === "asc" ? "↑" : "↓"}
                  </Box>
                )}
                filter={renderValueFilter(column)}
              />
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {sortedFlatData.map((row, index) => (
          <Tr key={index} bg={index % 2 === 0 ? "#444" : "#333"} py={pageStore.rowHeight}>
            {availableColumns.map(column => (
              <Td
                key={column.key}
                px={4}
                py={pageStore.rowHeight}
                textAlign={column.isNumeric ? "right" : "left"}
                fontStyle={row.active ? "normal" : "italic"}
                {...textCellAttributes(row[column.key], column)}
              >
                {formatCellValue(column.key, row[column.key], column.isNumeric)}
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const buttons = (
    <>
      <Button onClick={handleExportToXLSX} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} mr={3}>
        Экспорт в Excel
      </Button>
      <Button onClick={expandAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} mr={3}>
        Развернуть всё
      </Button>
      <Button onClick={collapseAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }}>
        Свернуть всё
      </Button>
    </>
  )

  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity>
        {renderButtons ? renderButtons(buttons) : buttons}
      </Collapse>
      {pageStore?.data_open_profit.length != 0 ?
        <Box w="100%">

          <SelfFilters />
          <ResetColumnFiltersButton filters={columnFilters} onReset={() => setColumnFilters({})} />

          <Box maxHeight="95vh" overflowX="auto" borderRadius="md" borderWidth={1} borderColor="gray.300">
            {groupedData ? (
              <Table tableLayout="auto" variant="simple" bg="" color="white" width="auto" maxWidth="fit-content">
                {renderTableHeader()}
                <Tbody>
                  {renderTotalRow()}
                  {sortedGroupedData?.map(renderGroupRow)}
                </Tbody>
              </Table>
            ) : (
              renderFlatTable()
            )}
          </Box>
        </Box>
        : null}
    </>
  );
});

export default TableData;
